import React, { useState, useEffect } from 'react';
import { riderAPI } from '../../lib/api';
import { getTierColor } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import { Gift, Star, Trophy, Crown, Diamond, Loader2 } from 'lucide-react';

const LoyaltyRewards = () => {
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    loadLoyalty();
  }, []);

  const loadLoyalty = async () => {
    try {
      const response = await riderAPI.getLoyalty();
      setLoyalty(response.data);
    } catch (error) {
      console.error('Error loading loyalty:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-red-600" />
      </div>
    );
  }

  const tierIcons = {
    bronze: Star,
    silver: Trophy,
    gold: Crown,
    platinum: Crown,
    diamond: Diamond,
  };

  const tierColors = {
    bronze: 'from-amber-600 to-amber-700',
    silver: 'from-slate-400 to-slate-500',
    gold: 'from-yellow-500 to-yellow-600',
    platinum: 'from-cyan-500 to-cyan-600',
    diamond: 'from-purple-500 to-purple-600',
  };

  const TierIcon = tierIcons[loyalty?.current_tier] || Star;

  const loyaltyProgress = loyalty?.next_tier 
    ? ((loyalty.total_rides - (loyalty.tiers[loyalty.current_tier]?.min_rides || 0)) / 
       (loyalty.tiers[loyalty.next_tier]?.min_rides - (loyalty.tiers[loyalty.current_tier]?.min_rides || 0))) * 100
    : 100;

  return (
    <div className="p-6" data-testid="loyalty-rewards-page">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-display font-bold text-slate-900 dark:text-white uppercase tracking-tight mb-8">
          Loyalty Rewards
        </h1>

        {/* Current Tier Card */}
        <Card className={`mb-8 bg-gradient-to-br ${tierColors[loyalty?.current_tier] || tierColors.bronze} border-0 text-white overflow-hidden`}>
          <CardContent className="p-8 relative">
            <div className="absolute top-0 right-0 w-64 h-64 opacity-10">
              <TierIcon className="w-full h-full" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                  <TierIcon className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-white/80 text-sm uppercase tracking-wide">Current Tier</p>
                  <p className="text-4xl font-display font-bold capitalize">{loyalty?.current_tier || 'Bronze'}</p>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-8 mt-8">
                <div>
                  <p className="text-white/80 text-sm">Discount</p>
                  <p className="text-3xl font-display font-bold">{loyalty?.discount_percent || 0}%</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Total Rides</p>
                  <p className="text-3xl font-display font-bold">{loyalty?.total_rides || 0}</p>
                </div>
                <div>
                  <p className="text-white/80 text-sm">Points</p>
                  <p className="text-3xl font-display font-bold">{loyalty?.loyalty_points || 0}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Progress to Next Tier */}
        {loyalty?.next_tier && (
          <Card className="mb-8 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="font-display uppercase tracking-tight">
                Progress to {loyalty.next_tier}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Progress value={loyaltyProgress} className="h-4" />
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                  {loyalty.total_rides} rides completed
                </span>
                <span className="font-semibold text-slate-900 dark:text-white">
                  {loyalty.rides_to_next_tier} more rides to {loyalty.next_tier}
                </span>
              </div>
            </CardContent>
          </Card>
        )}

        {/* All Tiers */}
        <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
          <CardHeader>
            <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <Gift className="w-5 h-5 text-red-600" />
              All Loyalty Tiers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(loyalty?.tiers || {}).map(([tier, config]) => {
                const Icon = tierIcons[tier];
                const isCurrentTier = tier === loyalty?.current_tier;
                const isAchieved = loyalty?.total_rides >= config.min_rides;

                return (
                  <div
                    key={tier}
                    className={`
                      flex items-center justify-between p-4 rounded-xl border-2 transition-all
                      ${isCurrentTier 
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20' 
                        : isAchieved
                          ? 'border-green-500/50 bg-green-50/50 dark:bg-green-900/10'
                          : 'border-slate-200 dark:border-slate-700'
                      }
                    `}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`
                        w-12 h-12 rounded-xl flex items-center justify-center
                        ${isAchieved ? 'bg-green-100 dark:bg-green-900/30' : 'bg-slate-100 dark:bg-slate-700'}
                      `}>
                        <Icon className={`w-6 h-6 ${getTierColor(tier)}`} />
                      </div>
                      <div>
                        <p className={`font-display font-bold uppercase ${getTierColor(tier)}`}>
                          {tier}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {config.min_rides}+ rides
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-display font-bold text-slate-900 dark:text-white">
                        {config.discount * 100}%
                      </p>
                      <p className="text-sm text-slate-500 dark:text-slate-400">discount</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-6 p-4 bg-slate-50 dark:bg-slate-700/50 rounded-xl">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                <Gift className="w-4 h-4 inline mr-1 text-red-600" />
                <strong>How it works:</strong> Your loyalty discount is applied automatically to every ride. 
                The discount is deducted from the driver's mileage rate, so you save while drivers earn their fair share!
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default LoyaltyRewards;
